import React from 'react';
import { CreditCard, Smartphone } from 'lucide-react';
import { PricingPlan, PaymentDetails } from '../../types/profile';

// Configuration des codes PIN Mobile Money
const PIN_CONFIG = {
  minLength: 4,
  maxLength: 8,
  placeholder: '••••••••'
};

// Constantes pour les méthodes de paiement
const PAYMENT_METHODS = [
  {
    id: 'card' as const,
    name: 'Carte bancaire / Mosolo',
    description: 'Visa, MasterCard, Mosolo',
    icon: CreditCard
  },
  {
    id: 'mobile' as const,
    name: 'Mobile Money',
    description: 'Airtel Money, M-Pesa, Orange Money',
    icon: Smartphone
  }
];

// Cartes acceptées avec logos professionnels
const ACCEPTED_CARDS = [
  { name: 'VISA', bgColor: 'bg-blue-600', textColor: 'text-white' },
  { name: 'MC', bgColor: 'bg-red-500', textColor: 'text-white' },
  { name: 'MOSOLO', bgColor: 'bg-green-600', textColor: 'text-white' }
];

// Fournisseurs Mobile Money avec logos
const MOBILE_MONEY_PROVIDERS = [
  {
    id: 'airtel',
    name: 'Airtel Money',
    logo: '/src/assets/images/airtel-logo.png'
  },
  {
    id: 'mpesa',
    name: 'M-Pesa',
    logo: '/src/assets/images/mpesa-logo.png'
  },
  {
    id: 'orange',
    name: 'Orange Money',
    logo: '/src/assets/images/orange-logo.png'
  }
];

// Pays supportés avec codes et drapeaux
const COUNTRIES = [
  { code: '+243', name: 'RD Congo', flag: '🇨🇩' },
  { code: '+250', name: 'Rwanda', flag: '🇷🇼' },
  { code: '+256', name: 'Uganda', flag: '🇺🇬' },
  { code: '+255', name: 'Tanzania', flag: '🇹🇿' },
  { code: '+254', name: 'Kenya', flag: '🇰🇪' }
];

// Périodes de facturation avec réductions
const BILLING_PERIODS = [
  {
    id: 'monthly' as const,
    name: 'Mensuel',
    multiplier: 1,
    discount: 0
  },
  {
    id: 'annual' as const,
    name: 'Annuel',
    multiplier: 12,
    discount: 0.15 // 15% de réduction
  }
];

// Codes promo prédéfinis
const PROMO_CODES = {
  'WELCOME10': 0.10,
  'SAVE20': 0.20,
  'NEWUSER': 0.15,
  'ANNUAL25': 0.25
};

// Valeurs par défaut
export const DEFAULT_PAYMENT_TYPE = 'card';
export const DEFAULT_COUNTRY_CODE = '+243';

interface PaymentModalProps {
  plan: PricingPlan | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  paymentType: 'card' | 'mobile' | 'manual';
  setPaymentType: (type: 'card' | 'mobile' | 'manual') => void;
  paymentInfo: PaymentDetails;
  setPaymentInfo: (info: PaymentDetails) => void;
  error: string;
  billingPeriod: 'monthly' | 'annual';
  setBillingPeriod: (period: 'monthly' | 'annual') => void;
  promoCode: string;
  setPromoCode: (code: string) => void;
  promoDiscount: number;
  onValidatePromo: (code: string) => number;
  onSetPromoDiscount: (discount: number) => void;
}

export default function PaymentModal({
  plan, 
  isOpen, 
  onClose, 
  onSubmit,
  paymentType,
  setPaymentType,
  paymentInfo,
  setPaymentInfo,
  error,
  billingPeriod,
  setBillingPeriod,
  promoCode,
  setPromoCode,
  promoDiscount,
  onValidatePromo,
  onSetPromoDiscount
}: PaymentModalProps) {
  if (!isOpen || !plan) return null;

  // Calcul du prix avec période et réductions
  const selectedBillingPeriod = BILLING_PERIODS.find(p => p.id === billingPeriod)!;
  const basePrice = parseFloat(plan.price) || 0;
  const periodPrice = basePrice * selectedBillingPeriod.multiplier;
  const periodDiscount = periodPrice * selectedBillingPeriod.discount;
  const promoDisountAmount = (periodPrice - periodDiscount) * promoDiscount;
  const finalPrice = periodPrice - periodDiscount - promoDisountAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md mx-auto max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Paiement - {plan.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
          {/* Sélection de la période */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Période de facturation
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {BILLING_PERIODS.map((period) => (
                <label key={period.id} className={`relative flex items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  billingPeriod === period.id 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="billingPeriod"
                    value={period.id}
                    checked={billingPeriod === period.id}
                    onChange={(e) => setBillingPeriod(e.target.value as any)}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">{period.name}</div>
                    {period.discount > 0 && (
                      <div className="text-xs text-green-600 font-medium">
                        -{Math.round(period.discount * 100)}%
                      </div>
                    )}
                  </div>
                  {period.discount > 0 && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                      Promo
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Résumé des prix */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Prix de base ({selectedBillingPeriod.name.toLowerCase()})</span>
              <span>${periodPrice.toFixed(2)}</span>
            </div>
            {selectedBillingPeriod.discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Réduction annuelle (-{Math.round(selectedBillingPeriod.discount * 100)}%)</span>
                <span>-${periodDiscount.toFixed(2)}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Code promo (-{Math.round(promoDiscount * 100)}%)</span>
                <span>-${promoDisountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-2">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">Total</span>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">${finalPrice.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">
                    {billingPeriod === 'annual' ? 'par an' : 'par mois'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Sélection de la méthode de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Méthode de paiement
            </label>
            <div className="space-y-3">
              {PAYMENT_METHODS.map((method) => {
                const IconComponent = method.icon;
                return (
                  <label key={method.id} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    paymentType === method.id 
                      ? 'border-primary bg-primary/5 shadow-sm' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={paymentType === method.id}
                      onChange={(e) => setPaymentType(e.target.value as any)}
                      className="sr-only"
                    />
                    <div className={`flex items-center justify-center w-10 h-10 rounded-lg mr-4 ${
                      paymentType === method.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <IconComponent size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{method.name}</div>
                      <div className="text-sm text-gray-500">{method.description}</div>
                    </div>
                    {paymentType === method.id && (
                      <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Code promo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code de promotion (optionnel)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Entrez votre code promo"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => {
                  if (promoCode.trim()) {
                    const discount = onValidatePromo(promoCode.trim());
                    onSetPromoDiscount(discount);
                    if (discount === 0) {
                      // Code invalide - vous pourriez afficher un message d'erreur ici
                      console.log('Code promo invalide');
                    }
                  }
                }}
                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Appliquer
              </button>
            </div>
            {promoDiscount > 0 && (
              <p className="text-sm text-green-600 mt-1 flex items-center">
                ✓ Code promo appliqué - {Math.round(promoDiscount * 100)}% de réduction
              </p>
            )}
          </div>

          {/* Champs selon la méthode */}
          {paymentType === 'card' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Informations de la carte
                </label>
                
                {/* Logos des cartes acceptées */}
                <div className="flex items-center gap-3 mb-3 p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Cartes acceptées:</span>
                  <div className="flex items-center gap-2">
                    {ACCEPTED_CARDS.map((card) => (
                      <div 
                        key={card.name}
                        className={`${card.bgColor} ${card.textColor} px-2 py-1 rounded text-xs font-bold`}
                      >
                        {card.name}
                      </div>
                    ))}
                  </div>
                </div>
                
                <input
                  type="text"
                  placeholder="Numéro de carte (Visa/MasterCard/Mosolo)"
                  value={paymentInfo.cardNumber}
                  onChange={(e) => setPaymentInfo({...paymentInfo, cardNumber: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="MM/AA"
                  value={paymentInfo.expiryDate}
                  onChange={(e) => setPaymentInfo({...paymentInfo, expiryDate: e.target.value})}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  placeholder="CVV"
                  value={paymentInfo.cvv}
                  onChange={(e) => setPaymentInfo({...paymentInfo, cvv: e.target.value})}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
            </div>
          )}

          {paymentType === 'mobile' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Choisir votre opérateur
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {MOBILE_MONEY_PROVIDERS.map((provider) => (
                    <label key={provider.id} className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      paymentInfo.mobileMoneyProvider === provider.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="mobileProvider"
                        value={provider.id}
                        checked={paymentInfo.mobileMoneyProvider === provider.id}
                        onChange={(e) => setPaymentInfo({...paymentInfo, mobileMoneyProvider: e.target.value})}
                        className="sr-only"
                      />
                      <img 
                        src={provider.logo} 
                        alt={provider.name}
                        className="w-8 h-8 object-contain mr-3"
                      />
                      <span className="font-medium text-gray-900">{provider.name}</span>
                      {paymentInfo.mobileMoneyProvider === provider.id && (
                        <div className="ml-auto w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Numéro de téléphone
                  </label>
                  <div className="flex">
                    <select 
                      value={paymentInfo.countryCode || DEFAULT_COUNTRY_CODE}
                      onChange={(e) => setPaymentInfo({...paymentInfo, countryCode: e.target.value})}
                      className="rounded-l-lg border border-gray-300 border-r-0 bg-gray-50 px-3 py-3 focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    >
                      {COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      placeholder="820123456"
                      value={paymentInfo.mobileNumber}
                      onChange={(e) => setPaymentInfo({...paymentInfo, mobileNumber: e.target.value})}
                      className="flex-1 rounded-r-lg border border-gray-300 border-l-0 p-3 focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Format: numéro sans le code pays (ex: 820123456)
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code PIN Mobile Money
                  </label>
                  <input
                    type="password"
                    placeholder={PIN_CONFIG.placeholder}
                    value={paymentInfo.mobilePin}
                    onChange={(e) => setPaymentInfo({...paymentInfo, mobilePin: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    minLength={PIN_CONFIG.minLength}
                    maxLength={PIN_CONFIG.maxLength}
                    pattern="[0-9]*"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Code PIN de {PIN_CONFIG.minLength} à {PIN_CONFIG.maxLength} chiffres
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            >
              Payer ${finalPrice.toFixed(2)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Export des types et constantes nécessaires
export { PROMO_CODES };